/**
 * Hauler user provisioning hook (quick 260814-k6a / ClickUp 86bbed5r5).
 *
 * POST /v1/users — a Dispatch Manager (or higher) adds a driver or office user
 * to their OWN tenant. MANAGE_USERS-gated server-side; the nav gating is UX only.
 *
 * The endpoint takes the tenant from the caller's JWT, so there is deliberately
 * no tenantId in this body — the UI cannot target another tenant even by mistake.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts } from '@lodex-pro/web-shared';

/** Roles a hauler admin may create. Mirrors CREATABLE_ROLES on the server. */
export const CREATABLE_USER_ROLES = [
  { value: 'DRIVER', label: 'Driver' },
  { value: 'LEAD_DRIVER', label: 'Lead Driver' },
  { value: 'DISPATCHER', label: 'Dispatcher' },
  { value: 'DISPATCH_MANAGER', label: 'Dispatch Manager' },
  // DEC-313 — billing roles, assignable in-product for the first time. Until now
  // the only way to get somebody into web-billing was a hand-written
  // tenant_users row, so every billing login in dev came from a fixture.
  { value: 'BILLING_STAFF', label: 'Billing Staff' },
  { value: 'BILLING_MANAGER', label: 'Billing Manager' },
  // DEC-313 — combined office roles, for the person who both dispatches and
  // bills. Each grants the exact union of its two halves. Both halves are named
  // in full because "Dispatcher + Billing" would not say which billing tier.
  { value: 'DISPATCHER_BILLING_STAFF', label: 'Dispatcher + Billing Staff' },
  { value: 'DISPATCHER_BILLING_MANAGER', label: 'Dispatcher + Billing Manager' },
  { value: 'DISPATCH_MANAGER_BILLING_STAFF', label: 'Dispatch Manager + Billing Staff' },
  { value: 'DISPATCH_MANAGER_BILLING_MANAGER', label: 'Dispatch Manager + Billing Manager' },
] as const;

export type CreatableUserRole = (typeof CREATABLE_USER_ROLES)[number]['value'];

/** Drivers sign in by SMS; office users by email + password. */
export function isDriverRole(role: CreatableUserRole): boolean {
  return role === 'DRIVER' || role === 'LEAD_DRIVER';
}

export interface CreateUserBody {
  readonly role: CreatableUserRole;
  readonly fullName: string;
  /**
   * Drivers only — a plain 10-digit US number as typed. NEVER prefixed with
   * "+1" here (86bbed5r5 item 5): the server canonicalizes with the same shared
   * helper the mobile login screen uses, so there is exactly one normalization
   * in the system.
   */
  readonly phone?: string;
  /** Office users only. A driver's address is synthesized server-side. */
  readonly email?: string;
}

export interface CreateUserResult {
  readonly id: string;
  readonly role: CreatableUserRole;
  readonly fullName: string;
  readonly email: string;
  readonly phone: string | null;
  readonly signInMethod: 'SMS_OTP' | 'EMAIL_PASSWORD';
  /**
   * Office users only. false = the account exists but the temporary-password
   * email did not send, so they cannot sign in until an admin uses Reset
   * password. Creation deliberately still succeeds -- see the route header.
   */
  readonly inviteEmailed?: boolean;
}

export function useCreateUser(
  onDone: (result: CreateUserResult) => void,
): ReturnType<typeof useApiMutation<CreateUserBody, CreateUserResult>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<CreateUserBody, CreateUserResult>(opts, '/v1/users', 'POST', {
    onSuccess: (result) => {
      // Invalidate the SHARED ['drivers'] prefix, not one specific key.
      //
      // There are two independent driver queries with different keys —
      // ['drivers','roster'] (dispatch board assignment pickers) and
      // ['drivers','fleet'] (the Fleet → Drivers list). TanStack matches
      // queryKey by prefix, so the bare ['drivers'] invalidates both.
      //
      // Invalidating only one is the silent failure here: the driver is created
      // successfully, the toast says so, and the list the manager is looking at
      // simply does not change until they reload — which reads as "it didn't
      // work" and invites a duplicate submission.
      void qc.invalidateQueries({ queryKey: ['drivers'] });
      onDone(result);
    },
  });
}
